import { useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

/**
 * Custom hook for handling API errors similar to the original commonAjaxFailFunc
 * @param {Function} onError - Optional callback function to execute after error handling
 * @returns {Object} Error handling functions and state
 */
export const useApiError = (onError) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [messageType, setMessageType] = useState('error') // 'error', 'success', 'warning', 'info'
  const navigate = useNavigate()
  const location = useLocation()

  /**
   * Handle API errors similar to commonAjaxFailFunc
   * @param {Error} error - The error object from axios
   * @param {string} customMessage - Optional custom error message
   * @param {Function} customCallback - Optional custom callback function
   */
  const handleApiError = useCallback((error, customMessage = null, customCallback = null) => {
    console.error('API Error:', error)

    // Handle parser error (similar to original code)
    if (error.message === 'parsererror' || error.code === 'PARSE_ERROR') {
      // mobile画面の場合は/appLoginに遷移
      if (location.pathname.startsWith('/app')) {
        navigate('/appLogin')
      } else {
        navigate('/login')
      }
      return
    }

    let message = ''

    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status
      const statusText = error.response.statusText
      
      if (customMessage) {
        message = customMessage
      } else if (status === 406) {
        message = '認証トークンに問題があります。再ログインしてください。'
        // mobile画面の場合は/appLoginに遷移
        if (location.pathname.startsWith('/app')) {
          navigate('/appLogin')
        } else {
          navigate('/login')
        }
        return
      } else if (status === 403) {
        message = 'アクセス権限がありません。'
      } else if (status === 404) {
        message = 'リソースが見つかりません。'
      } else if (status === 500) {
        message = 'サーバーエラーが発生しました。'
      } else {
        message = `${status} ${statusText} : エラーが発生しました。`
      }

      // Check for business logic errors (similar to original success check)
      if (error.response.data && error.response.data.returnCode && error.response.data.returnCode !== '200') {
        if (error.response.data.message) {
          message = error.response.data.message
        } else if (error.response.data.errMessage) {
          message = error.response.data.errMessage
        }
      }
    } else if (error.request) {
      // Network error
      message = 'ネットワークエラーが発生しました。接続を確認してください。'
    } else {
      // Other errors
      message = error.message || '予期しないエラーが発生しました。'
    }

    // Set error state
    setErrorMessage(message)
    setMessageType('error')
    setShowErrorDialog(true)

    // Execute custom callback if provided
    if (customCallback && typeof customCallback === 'function') {
      customCallback()
    } else if (onError && typeof onError === 'function') {
      onError()
    }
  }, [navigate, location.pathname, onError])

  /**
   * Handle API response with business logic error codes (for HTTP 200 responses with error codes)
   * @param {Object} response - The axios response object
   * @param {string} customMessage - Optional custom error message
   * @param {Function} customCallback - Optional custom callback function
   * @returns {boolean} - Returns true if there was an error, false if successful
   */
  const handleApiResponse = useCallback((response, customMessage = null, customCallback = null) => {
    if (!response) {
      return false
    }

    const { returnCode, message: responseMessage, errMessage, validationErrorList, messageList } = response

    // Check if response indicates an error (non-200 return code)
    if (returnCode && returnCode !== '200') {
      let errorMessage = ''

      // Handle different error codes based on your GlobalExceptionHandler
      switch (returnCode) {
        case '400':
          errorMessage = responseMessage || '入力エラーが発生しました。'
          break
        case '600':
          errorMessage = responseMessage || 'エラーが発生しました。'
          break
        case '999':
          errorMessage = responseMessage || 'システムエラーが発生しました。'
          break
        default:
          errorMessage = customMessage || responseMessage || errMessage || 'エラーが発生しました。'
      }

      // Handle validation errors if present
      if (validationErrorList && validationErrorList.length > 0) {
        const validationMessages = validationErrorList.map(error => error.message).join('\n')
        errorMessage = errorMessage + '\n\n' + validationMessages
      }

      // Handle message list if present
      if (messageList && messageList.length > 0) {
        const additionalMessages = messageList.join('\n')
        errorMessage = errorMessage + '\n\n' + additionalMessages
      }

      // Set error state
      setErrorMessage(errorMessage.trim())
      setMessageType('error')
      setShowErrorDialog(true)

      // Execute custom callback if provided
      if (customCallback && typeof customCallback === 'function') {
        customCallback()
      } else if (onError && typeof onError === 'function') {
        onError()
      }

      return true // Indicates there was an error
    }

    return false // Indicates no error
  }, [onError])

  /**
   * Close error dialog
   */
  const closeErrorDialog = useCallback(() => {
    setShowErrorDialog(false)
    setErrorMessage('')
    setMessageType('error')
  }, [])

  /**
   * Show success message
   * @param {string} message - Success message to display
   */
  const showSuccessMessage = useCallback((message) => {
    setErrorMessage(message)
    setMessageType('success')
    setShowErrorDialog(true)
  }, [])

  /**
   * Show warning message
   * @param {string} message - Warning message to display
   */
  const showWarningMessage = useCallback((message) => {
    setErrorMessage(message)
    setMessageType('warning')
    setShowErrorDialog(true)
  }, [])

  /**
   * Show info message
   * @param {string} message - Info message to display
   */
  const showInfoMessage = useCallback((message) => {
    setErrorMessage(message)
    setMessageType('info')
    setShowErrorDialog(true)
  }, [])

  /**
   * Handle validation errors (similar to original validation error handling)
   * @param {Object} validationErrors - Object with field names as keys and error messages as values
   */
  const handleValidationErrors = useCallback((validationErrors) => {
    let message = ''
    
    if (typeof validationErrors === 'object') {
      Object.values(validationErrors).forEach(error => {
        if (error) {
          message += error + '\n'
        }
      })
    } else if (Array.isArray(validationErrors)) {
      validationErrors.forEach(error => {
        if (error) {
          message += error + '\n'
        }
      })
    } else if (typeof validationErrors === 'string') {
      message = validationErrors
    }

    if (message) {
      setErrorMessage(message.trim())
      setMessageType('error')
      setShowErrorDialog(true)
    }
  }, [])

  return {
    showErrorDialog,
    errorMessage,
    messageType,
    handleApiError,
    handleApiResponse,
    handleValidationErrors,
    closeErrorDialog,
    showSuccessMessage,
    showWarningMessage,
    showInfoMessage
  }
}

